import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const pipelineStages = [
  {
    id: 'pool',
    label: '500 Candidates',
    short: 'Pool',
    icon: 'Users',
    test: 'Resume + profile upload',
    filter: null,
    count: '500',
    color: 'text-[var(--lp-ink-soft)]',
  },
  {
    id: 'screening',
    label: 'AI Screening',
    short: 'Screen',
    icon: 'ScanSearch',
    test: 'Skills match, experience fit, role alignment',
    filter: 'Score ≥ 70 → proceed',
    count: '180',
    color: 'text-emerald-500',
  },
  {
    id: 'dsa',
    label: 'DSA Round',
    short: 'DSA',
    icon: 'Code',
    test: 'Algorithms, data structures, problem-solving',
    filter: 'Coding score ≥ 65 → proceed',
    count: '72',
    color: 'text-indigo-500',
  },
  {
    id: 'ml',
    label: 'ML Round',
    short: 'ML',
    icon: 'Brain',
    test: 'Model design, ML fundamentals, system thinking',
    filter: 'Technical depth ≥ 60 → proceed',
    count: '38',
    color: 'text-violet-500',
  },
  {
    id: 'leadership',
    label: 'Leadership',
    short: 'Lead',
    icon: 'Briefcase',
    test: 'Teamwork, conflict resolution, strategic thinking',
    filter: 'Teamwork > 60 → proceed',
    count: '18',
    color: 'text-sky-500',
  },
  {
    id: 'hr',
    label: 'HR Round',
    short: 'HR',
    icon: 'MessageSquare',
    test: 'Culture fit, communication, compensation alignment',
    filter: 'Culture fit ≥ 75 → proceed',
    count: '8',
    color: 'text-amber-500',
  },
  {
    id: 'final',
    label: 'Top Candidate',
    short: 'Hire',
    icon: 'Trophy',
    test: 'Final scorecard + hiring committee review',
    filter: 'Best match selected',
    count: '1',
    color: 'text-[var(--lp-accent)]',
  },
];

const STAGE_COUNT = pipelineStages.length;
const SCROLL_HEIGHT_VH = STAGE_COUNT * 55;

const setupSteps = [
  {
    step: '01',
    title: 'Design your pipeline',
    body: 'Pick interview types per stage. Set scoring thresholds — e.g. teamwork > 60 in Leadership before candidates advance.',
    icon: 'GitBranch',
  },
  {
    step: '02',
    title: 'Upload candidate data',
    body: 'Bulk import resumes and profiles. prepxai parses skills, experience, and role fit before round one.',
    icon: 'Upload',
  },
  {
    step: '03',
    title: 'Let AI run the rounds',
    body: 'Candidates interview 24/7. Only top scorers move forward. You review finalists, not every applicant.',
    icon: 'Zap',
  },
];

const getConnectorFill = (progress, idx) => {
  const segmentStart = idx / (STAGE_COUNT - 1);
  const segmentEnd = (idx + 1) / (STAGE_COUNT - 1);
  if (progress <= segmentStart) return 0;
  if (progress >= segmentEnd) return 1;
  return (progress - segmentStart) / (segmentEnd - segmentStart);
};

const BusinessPipelineSection = () => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const smoothProgress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 });
  const progressPercent = useTransform(smoothProgress, [0, 1], ['0%', '100%']);
  const mobileLineHeight = useTransform(smoothProgress, [0, 1], ['0%', '100%']);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const unsubscribe = smoothProgress.on('change', (v) => {
      setScrollProgress(v);
      const idx = Math.min(STAGE_COUNT - 1, Math.floor(v * STAGE_COUNT));
      setActiveIndex(idx);
    });

    return unsubscribe;
  }, [smoothProgress, prefersReducedMotion]);

  const selected = pipelineStages[activeIndex];

  const getStageState = (idx) => {
    if (prefersReducedMotion) return idx === 0 ? 'active' : 'upcoming';
    if (idx < activeIndex) return 'passed';
    if (idx === activeIndex) return 'active';
    return 'upcoming';
  };

  return (
    <>
      <section
        id="pipeline"
        ref={containerRef}
        className="relative border-t border-[var(--lp-line)] bg-white"
        style={{ height: prefersReducedMotion ? 'auto' : `${SCROLL_HEIGHT_VH}vh` }}
      >
        <div
          className={`${
            prefersReducedMotion
              ? 'py-24 lg:py-32'
              : 'sticky top-16 flex h-[calc(100vh-4rem)] flex-col justify-center overflow-hidden py-6 lg:top-0 lg:h-screen lg:py-8'
          }`}
        >
          <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[var(--lp-accent)]">
                Hiring pipeline
              </p>
              <h2 className="mt-3 text-3xl tracking-tight text-[var(--lp-ink)] sm:text-4xl lg:text-5xl">
                Automate your hiring pipeline.
                <span className="block text-[var(--lp-accent)]">Interview 500, not 50.</span>
              </h2>
              {!prefersReducedMotion && (
                <p className="mt-3 hidden text-sm text-[var(--lp-ink-soft)] sm:block">
                  Scroll to watch candidates flow through each stage
                </p>
              )}
            </div>

            <div className="relative mt-8 lg:mt-12">
              {/* Desktop horizontal pipeline */}
              <div className="hidden items-start justify-between lg:flex">
                {pipelineStages.map((stage, idx) => {
                  const state = getStageState(idx);
                  const isActive = state === 'active';
                  const isPassed = state === 'passed';
                  const connectorFill = getConnectorFill(scrollProgress, idx);

                  return (
                    <div key={stage.id} className="relative flex flex-1 flex-col items-center">
                      <motion.div
                        animate={{
                          scale: isActive ? 1.12 : 1,
                          opacity: state === 'upcoming' ? 0.45 : 1,
                        }}
                        transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                        className="relative z-10 flex flex-col items-center gap-2"
                      >
                        <div
                          className={`relative flex h-14 w-14 items-center justify-center rounded-xl border transition-colors duration-300 ${
                            isActive
                              ? 'border-[var(--lp-accent)] bg-[var(--lp-accent)]/10'
                              : isPassed
                                ? 'border-[var(--lp-accent)]/40 bg-[var(--lp-accent)]/5'
                                : 'border-[var(--lp-line)] bg-white'
                          }`}
                        >
                          <Icon name={stage.icon} size={22} className={stage.color} />
                          {stage.count && idx > 0 && idx < STAGE_COUNT - 1 && (
                            <motion.span
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: isActive || isPassed ? 1 : 0.8, opacity: isActive || isPassed ? 1 : 0.5 }}
                              className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--lp-contrast-bg)] px-1 font-mono text-[9px] text-[var(--lp-contrast-text)]"
                            >
                              {stage.count}
                            </motion.span>
                          )}
                        </div>
                        <span
                          className={`font-mono text-[10px] tracking-wide transition-colors ${
                            isActive ? 'text-[var(--lp-accent)]' : 'text-[var(--lp-ink-soft)]'
                          }`}
                        >
                          {stage.short}
                        </span>
                      </motion.div>

                      {idx < STAGE_COUNT - 1 && (
                        <div className="absolute left-[calc(50%+30px)] top-[28px] h-px w-[calc(100%-60px)] bg-[var(--lp-line)]">
                          <motion.div
                            className="h-full origin-left bg-[var(--lp-accent)]"
                            animate={{ scaleX: connectorFill }}
                            transition={{ type: 'spring', stiffness: 200, damping: 30 }}
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile vertical pipeline */}
              <div className="relative mx-auto max-w-sm lg:hidden">
                <div className="absolute bottom-4 left-[27px] top-4 w-px bg-[var(--lp-line)]" />
                <motion.div
                  className="absolute left-[27px] top-4 w-px origin-top bg-[var(--lp-accent)]"
                  style={{ height: mobileLineHeight }}
                />

                <div className="space-y-5">
                  {pipelineStages.map((stage, idx) => {
                    const state = getStageState(idx);
                    const isActive = state === 'active';
                    const isPassed = state === 'passed';

                    return (
                      <motion.div
                        key={stage.id}
                        animate={{ opacity: state === 'upcoming' ? 0.4 : 1, x: isActive ? 4 : 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                        className="relative flex items-center gap-4"
                      >
                        <div
                          className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                            isActive
                              ? 'border-[var(--lp-accent)] bg-[var(--lp-accent)]/10'
                              : isPassed
                                ? 'border-[var(--lp-accent)]/40 bg-[var(--lp-accent)]/5'
                                : 'border-[var(--lp-line)] bg-white'
                          }`}
                        >
                          <Icon name={stage.icon} size={20} className={stage.color} />
                        </div>
                        <div>
                          <p className={`text-sm ${isActive ? 'text-[var(--lp-ink)]' : 'text-[var(--lp-ink-soft)]'}`}>
                            {stage.label}
                          </p>
                          {stage.count && idx > 0 && idx < STAGE_COUNT - 1 && (
                            <p className="font-mono text-[10px] text-[var(--lp-accent)]">{stage.count} remaining</p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>

            {!prefersReducedMotion && (
              <div className="mx-auto mt-6 hidden h-1 max-w-md overflow-hidden rounded-full bg-[var(--lp-line)] sm:block">
                <motion.div className="h-full rounded-full bg-[var(--lp-accent)]" style={{ width: progressPercent }} />
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto mt-6 max-w-2xl rounded-2xl border border-[var(--lp-line)] bg-white p-5 sm:mt-8 sm:p-7"
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    key={`icon-${selected.id}`}
                    initial={{ scale: 0.85, rotate: -8 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--lp-line)] bg-white"
                  >
                    <Icon name={selected.icon} size={20} className={selected.color} />
                  </motion.div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-xl text-[var(--lp-ink)]">{selected.label}</h3>
                      <span className="rounded-full bg-[var(--lp-accent)]/10 px-2.5 py-0.5 font-mono text-[10px] text-[var(--lp-accent)]">
                        Stage {activeIndex + 1}/{STAGE_COUNT}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-[var(--lp-ink-soft)]">
                      <span className="text-[var(--lp-ink)]">What we test:</span> {selected.test}
                    </p>
                    {selected.filter && (
                      <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-[var(--lp-line)] bg-white px-3 py-1 font-mono text-[11px] text-[var(--lp-accent)]">
                        <Icon name="Filter" size={12} />
                        {selected.filter}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {!prefersReducedMotion && activeIndex < STAGE_COUNT - 1 && (
              <motion.p
                animate={{ opacity: [0.35, 0.75, 0.35] }}
                transition={{ repeat: Infinity, duration: 2.5 }}
                className="mt-5 text-center font-mono text-[10px] tracking-widest text-[var(--lp-ink-soft)]"
              >
                ↓ keep scrolling
              </motion.p>
            )}
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--lp-line)] bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <p className="mb-10 text-center text-base leading-relaxed text-[var(--lp-ink-soft)]">
            Build multi-stage pipelines with custom scoring filters. Upload candidate data and resumes — prepxai tells you what to test at each round and who moves forward.
          </p>
          <div className="grid gap-5 sm:grid-cols-3">
            {setupSteps.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-[var(--lp-line)] bg-white p-6"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="font-mono text-xs text-[var(--lp-accent)]">{item.step}</span>
                  <Icon name={item.icon} size={18} className="text-[var(--lp-ink-soft)]" />
                </div>
                <h4 className="text-lg text-[var(--lp-ink)]">{item.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[var(--lp-ink-soft)]">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BusinessPipelineSection;
